import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { CircleCheck as CheckCircle, ArrowRight, Phone, Target, Server, Box, Terminal, Database, HardDrive, Network, Layers, Monitor } from 'lucide-react';

const VirtualisierungLinux = () => {
  const seoJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Virtualisierung & Linux-Administration Mönchengladbach',
    description: 'Virtualisierung (Proxmox, VMware, Hyper-V), Docker-Container und Linux-Administration für KMU in Mönchengladbach und Umgebung.',
    provider: { '@type': 'LocalBusiness', name: 'Schteffann', url: 'https://schteffann.de/' },
    areaServed: ['Mönchengladbach', 'Viersen', 'Krefeld', 'Neuss', 'Düsseldorf', 'Nordrhein-Westfalen'],
    serviceType: 'IT-Dienstleistungen'
  };

  const probleme = [
    'Jeder Server läuft auf eigener Hardware – teuer und unflexibel',
    'Kein Know-how für Linux, obwohl viele Tools darauf basieren',
    'Testumgebungen aufsetzen dauert ewig',
    'Datenbanken laufen unsicher oder ohne Backup-Konzept',
    'Container-Technologien klingen interessant, aber niemand weiß, wo anfangen'
  ];

  const virtualisierung = [
    {
      icon: Layers,
      name: 'Proxmox VE',
      text: 'Open-Source-Virtualisierung auf Enterprise-Niveau. Mehrere Server und Container auf einer Hardware – kosteneffizient und leistungsstark.'
    },
    {
      icon: Monitor,
      name: 'VMware vSphere',
      text: 'Der Industriestandard für Unternehmens-Virtualisierung. Migration, Verwaltung und Optimierung bestehender VMware-Umgebungen.'
    },
    {
      icon: Server,
      name: 'Hyper-V',
      text: 'Microsofts Virtualisierungsplattform – ideal, wenn Sie bereits Windows Server einsetzen. Nahtlose Integration in Ihre Microsoft-Welt.'
    },
    {
      icon: Box,
      name: 'Docker Container',
      text: 'Anwendungen isoliert und portabel betreiben. Schnelles Deployment, einfache Skalierung, konsistente Umgebungen.'
    }
  ];

  const linuxKompetenzen = [
    { icon: Terminal, text: 'Ubuntu Server – Installation, Konfiguration, Härtung' },
    { icon: Network, text: 'Netzwerkdienste – DNS, DHCP, Reverse Proxy (Nginx)' },
    { icon: HardDrive, text: 'Backup- und Wiederherstellungskonzepte für Linux-Systeme' },
    { icon: Server, text: 'Automatisierung mit Bash-Scripting und Cron-Jobs' },
    { icon: Layers, text: 'Monitoring und Log-Management' }
  ];

  const datenbanken = [
    {
      icon: Database,
      name: 'MariaDB / MySQL',
      text: 'Der bewährte Open-Source-Klassiker. Installation, Optimierung, Backup und Replikation.'
    },
    {
      icon: Database,
      name: 'Supabase',
      text: 'Die moderne Open-Source-Alternative zu Firebase. Backend-as-a-Service mit PostgreSQL, Auth und Echtzeit-Funktionen.'
    },
    {
      icon: Database,
      name: 'Microsoft SQL Server',
      text: 'Für bestehende Microsoft-Umgebungen. Administration, Performance-Tuning und Integration mit Ihren Geschäftsanwendungen.'
    }
  ];

  const beispiele = [
    {
      title: 'Agentur',
      text: 'Proxmox-Server mit Docker-Containern für Webprojekte. Jeder Kunde hat seine eigene, isolierte Umgebung. Deployment per Knopfdruck.'
    },
    {
      title: 'Handwerksbetrieb',
      text: 'Hyper-V-Server für ERP und Dateiablage. Getrennte virtuelle Maschinen für Buchhaltung und Werkstatt-PCs. Zentrales Backup.'
    },
    {
      title: 'Arztpraxis',
      text: 'Ubuntu-Server als Backup-Ziel mit verschlüsselter Speicherung. MariaDB für die Terminverwaltung. Automatische Datensicherung nachts.'
    }
  ];

  const liefere = [
    'Bestandsaufnahme: Welche Systeme haben Sie, was brauchen Sie wirklich?',
    'Konzept: Virtualisierungsstrategie passend zu Ihrer Unternehmensgröße',
    'Umsetzung: Aufbau und Migration – Schritt für Schritt, nicht alles auf einmal',
    'Dokumentation: Verständliche Anleitungen für Ihr Team',
    'Wartung: Regelmäßige Updates, Monitoring, Backup-Checks'
  ];

  const relatedPages = [
    { title: 'IT-Infrastruktur für KMU', url: '/it-infrastruktur-kmu', description: 'Server, Netzwerk und Backup – die Basis Ihrer IT' },
    { title: 'IT-Sicherheit & ISMS', url: '/it-sicherheit-isms', description: 'Sichere Konfiguration Ihrer Systeme' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEOHead
        title="Virtualisierung & Linux-Administration Mönchengladbach – Schteffann"
        description="Virtualisierung (Proxmox, VMware, Hyper-V), Docker-Container und Linux-Administration für KMU in Mönchengladbach, Viersen & Krefeld. Datenbanken: MariaDB, MS-SQL."
        keywords="Virtualisierung Mönchengladbach, Linux Administration Mönchengladbach, Proxmox, VMware, Hyper-V, Docker, Ubuntu Server, MariaDB, MS-SQL, Container, KMU IT, Viersen, Krefeld, NRW"
        jsonLd={seoJsonLd}
      />
      {/* Hero */}
      <section className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-br from-cyan-50/50 via-blue-50/30 to-slate-50">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-600/5"></div>
        <div className="container relative">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200 px-6 py-2">
              <Server className="w-4 h-4 mr-2" />
              Zusatzkompetenz
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-slate-900">Virtualisierung & Linux-Administration in </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700">Mönchengladbach</span>
            </h1>
            <p className="text-xl lg:text-2xl text-slate-600 leading-relaxed">
              Nicht nur Microsoft – ich bringe auch Erfahrung mit Virtualisierung, Docker-Containern, Linux-Servern und Datenbanken mit. Für KMU in Mönchengladbach, Viersen und Krefeld, die flexibel und kosteneffizient arbeiten wollen.
            </p>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Kennen Sie das?</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {probleme.map((problem, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{problem}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-lg text-slate-700 mt-8 font-medium">
                  Es gibt pragmatische Lösungen – auch für kleinere Unternehmen.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Virtualisierung */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Virtualisierung – mehr aus Ihrer Hardware herausholen
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Statt für jeden Dienst einen eigenen Server zu kaufen, laufen mehrere virtuelle Maschinen auf einer leistungsstarken Hardware.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {virtualisierung.map((item, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4 mb-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all">
                      <item.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 pt-2">{item.name}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Linux */}
      <section className="py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                Linux-Administration mit Ubuntu
              </h2>
              <p className="text-xl text-slate-600">
                Linux ist das Rückgrat vieler moderner IT-Dienste. Ich kenne mich aus – ohne Berührungsängste.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {linuxKompetenzen.map((item, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all flex-shrink-0">
                        <item.icon className="h-6 w-6 text-cyan-600" />
                      </div>
                      <p className="text-slate-700 leading-relaxed pt-2">{item.text}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Datenbanken */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Datenbanken – Ihre Daten richtig verwalten
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Egal ob klassisch oder modern – ich helfe bei Auswahl, Einrichtung und Betrieb der passenden Datenbank.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {datenbanken.map((item, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-slate-200 hover:border-cyan-200 bg-white">
                <CardContent className="p-8">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-3 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl group-hover:from-cyan-200 group-hover:to-blue-200 transition-all">
                      <item.icon className="h-6 w-6 text-cyan-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">{item.name}</h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed">{item.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Beispiele */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Beispiele aus der Praxis
            </h2>
            <p className="text-xl text-slate-600">
              So setzen andere Unternehmen Virtualisierung und Linux ein:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {beispiele.map((beispiel, index) => (
              <Card key={index} className="border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-3">{beispiel.title}</h3>
                  <p className="text-slate-600">{beispiel.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Was Sie bekommen */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-cyan-50/30">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Was Sie von mir bekommen</h2>
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="space-y-4">
                  {liefere.map((item, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 text-cyan-600 mt-1 flex-shrink-0" />
                      <p className="text-lg text-slate-700">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-20">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Das könnte Sie auch interessieren</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {relatedPages.map((page, index) => (
              <Link key={index} to={page.url}>
                <Card className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-cyan-200 h-full">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors mb-3">
                      {page.title}
                    </h3>
                    <p className="text-slate-600 mb-4">{page.description}</p>
                    <div className="flex items-center text-cyan-600 group-hover:text-cyan-700">
                      <span className="text-sm font-medium">Mehr erfahren</span>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container">
          <Card className="bg-gradient-to-br from-cyan-600 via-blue-600 to-cyan-700 border-0 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
            <CardContent className="p-12 lg:p-16 relative text-white text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Virtualisierung oder Linux-Projekt geplant?
              </h2>
              <p className="text-xl text-cyan-100 mb-10 max-w-2xl mx-auto">
                Lassen Sie uns sprechen – ich sage Ihnen ehrlich, was Sinn macht und was nicht.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button size="lg" asChild className="bg-white text-cyan-700 hover:bg-cyan-50 shadow-xl px-8 py-4 text-lg">
                  <Link to="/contact">
                    <Target className="mr-2 h-5 w-5" />
                    Jetzt Gespräch vereinbaren
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-4 text-lg">
                  <a href="tel:015679697252">
                    <Phone className="mr-2 h-5 w-5" />
                    0156-79697252
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default VirtualisierungLinux;
